import { PropsWithChildren } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Color } from '../../config/Color';
import { appOverride } from '../../util/AppUtils';
import { ChoiceItem } from '../flows/layouts/SingleSelectLayout';
import { Label } from './Typography';

export enum OptionButtonWidth {
  Middle = '70%',
  Wide = '100%',
}

type AnimationProps = {
  animationEnabled?: boolean;
  animationIndex?: number;
};

export type OptionButtonProps = PropsWithChildren<{
  value: string;
  selected?: boolean;
  width?: OptionButtonWidth;
}> &
  AnimationProps;

export const OptionFieldset = styled.fieldset`
  display: flex;
  flex-direction: column;
  align-items: center;

  > * + * {
    margin-top: ${({ theme }) => theme.spacing._20px};
  }
`;

export const OptionButtonInput = styled.input`
  margin: 0;
  -webkit-appearance: none;
  appearance: none;
  color: ${Color.flow.default};
  /* Start - Following needed for iOS */
  height: 1px;
  left: -10000px;
  position: absolute;
  width: 1px;
  /* End */
`;

// Animation configuration
// Buttons slide up from below with a staggered delay between each one
const ANIMATION_CONFIG = {
  duration: '0.6s',
  stagger: 0.1, // seconds between each button
  distance: 20, // pixels to slide up from
};

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(${ANIMATION_CONFIG.distance}px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const OptionButtonLabel = styled(Label)<{
  width: OptionButtonWidth;
  animationEnabled?: boolean;
  animationIndex?: number;
}>`
  /* Same as BodySM */
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 24px;
  border: solid 2px
    ${appOverride({ nerva: Color.action.accent, default: Color.decorative.default })};
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${appOverride({ nerva: Color.content.default, default: Color.flow.default })};

  width: ${({ width }) => appOverride({ nerva: '100%', default: width })};
  padding: ${({ theme }) => `${theme.spacing._10px} ${theme.spacing._16px}`};
  border-radius: ${appOverride({ nerva: '14px', default: '18px' })};
  overflow-x: hidden;
  cursor: pointer;
  word-break: keep-all;

  background: ${appOverride({ nerva: 'transparent', default: Color.surface.default })};
  transition: all 0.15s ease-in;

  &.selected,
  &.selected:focus-within {
    background: ${Color.flow.muted};
    border-color: ${appOverride({ nerva: Color.action.accent, default: 'transparent' })};
    color: ${appOverride({ nerva: Color.content.default, default: Color.flow.default })};
  }

  ${({ animationEnabled, animationIndex = 0 }) =>
    animationEnabled &&
    css`
      animation: ${fadeInUp} ${ANIMATION_CONFIG.duration} ease-out
        ${animationIndex * ANIMATION_CONFIG.stagger}s both;
    `}
`;

export function autoWidth(choices: ChoiceItem[]): OptionButtonWidth {
  // Chooses the button width for a set of a Option Buttons
  // based on the longest label length
  const labels = choices.map((choice) => {
    return typeof choice === 'object' ? choice.label : choice;
  });
  const WIDE_BREAKPOINT = 19;
  const maxChars = Math.max(...labels.map((x) => x.length));
  return maxChars >= WIDE_BREAKPOINT ? OptionButtonWidth.Wide : OptionButtonWidth.Middle;
}
